import React from "react";
import {
  Card,
  Avatar,
  Typography,
  Box,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { SxProps } from "@mui/system";
import image_for_coffe_call from "../../assets/Upstream.png";
import { LikeIcon, ThreeDotVerticalIcon } from "../../assets/reactIcons";

interface QueriesCardProps {
  username: string;
  email: string;
  content: string;
  timestamp: string;
  likes?: number;
  replies?: number;
  onLike?: () => void;
  onReply?: () => void;
  onMore?: () => void;
}

// Common styles that are reused
const commonStyles = {
  flexCenter: {
    display: "flex",
    alignItems: "center",
  },
  flexColumn: {
    display: "flex",
    flexDirection: "column",
  },
  neutralText: {
    color: "var(--neutral-500)",
  },
  hoverEffect: {
    "&:hover": {
      background: "rgba(0, 0, 0, 0.04)",
    },
  },
} as const;

// Component-specific styles
const styles = {
  card: {
    maxWidth: "100%",
    borderRadius: "12px",
    border: "1px solid var(--neutral-100)",
    background: "var(--neutral-50)",
    padding: "16px",
    ...commonStyles.flexColumn,
    alignItems: "flex-start",
    gap: "16px",
  } as SxProps,

  mainContainer: {
    ...commonStyles.flexCenter,
    gap: "12px",
    alignItems: "flex-start",
    width: "100%",
  } as SxProps,

  avatar: {
    width: 24,
    height: 24,
    borderRadius: "4px",
  } as SxProps,

  contentWrapper: {
    padding: 0,
    width: "100%",
    ...commonStyles.flexColumn,
    gap: "12px",
  } as SxProps,

  headerContainer: {
    ...commonStyles.flexCenter,
    justifyContent: "space-between",
    width: "100%",
  } as SxProps,

  userInfoContainer: {
    ...commonStyles.flexCenter,
    gap: "8px",
  } as SxProps,

  username: {
    fontSize: "18px",
    lineHeight: "24px",
    fontWeight: "500",
    color: "var(--neutralBlack)",
  } as SxProps,

  email: {
    ...commonStyles.neutralText,
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: "400",
  } as SxProps,

  timestamp: {
    ...commonStyles.neutralText,
    fontSize: "12px",
    lineHeight: "26px",
    fontWeight: "400",
  } as SxProps,

  contentBox: {
    ...commonStyles.flexColumn,
    gap: "8px",
  } as SxProps,

  mainContent: {
    color: "#111",
    fontSize: "0.875rem",
    lineHeight: "1.5",
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 3,
    overflow: "hidden",
  } as SxProps,

  actionButton: {
    color: "#666",
    textTransform: "none",
    ...commonStyles.hoverEffect,
  } as SxProps,

  likeButton: {
    ...commonStyles.flexCenter,
    color: "#666",
    textTransform: "none",
    ...commonStyles.hoverEffect,
  } as SxProps,

  actionContainer: {
    ...commonStyles.flexCenter,
    gap: 2,
  } as SxProps,

  timestampText: {
    color: "#666",
    ml: "auto",
    fontSize: "0.75rem",
  } as SxProps,

  likeIcon: {
    marginRight: "4px",
    color: "#6b6bff",
    fontSize: "20px",
  } as React.CSSProperties,
} as const;

export default function QueriesCard({
  username,
  email,
  content,
  timestamp,
  likes = 0,
  replies = 0,
  onLike,
  onReply,
  onMore,
}: QueriesCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Card sx={styles.card}>
      <Box sx={styles.mainContainer}>
        <Avatar src={image_for_coffe_call} alt={username} sx={styles.avatar} />
        <Box sx={styles.contentWrapper}>
          <Box sx={{ 
            ...styles.headerContainer,
            flexDirection: isMobile ? "column" : "row"
          }}>
            <Box sx={{ 
              ...styles.userInfoContainer, 
              gap: isMobile ? "4px" : "8px",
              flexDirection: isMobile ? "column" : "row"
            }}>
              <Typography variant="body1" sx={styles.username}>
                {username}
              </Typography>
              <Typography variant="body2" sx={styles.email}>
                {email}
              </Typography>
            </Box>
            <Box sx={{ 
              ...styles.userInfoContainer, 
              gap: isMobile ? "4px" : "8px",
            }}>
              <Typography variant="body1" sx={styles.timestamp}>
                {timestamp}
              </Typography>
              <Box onClick={onMore}>
                <ThreeDotVerticalIcon />
              </Box>
            </Box>
          </Box>
          <Box sx={styles.contentBox}>
            <Typography variant="body1" sx={styles.mainContent}>
              {content}
            </Typography>
            {content.split(" ").length > 30 && (
              <Button
                size="small"
                sx={styles.actionButton}
                onClick={() => {
                  const fullContent = document.createElement("div");
                  fullContent.innerText = content;
                  document.body.appendChild(fullContent);
                }}
              >
                Read More
              </Button>
            )}
          </Box>
          <Box sx={styles.actionContainer}>
            <Typography variant="body2" sx={styles.actionButton}>
              Like
            </Typography>
            <Button onClick={onLike} size="small" sx={styles.likeButton}>
              <LikeIcon style={styles.likeIcon} />
              {likes}
            </Button>
            <Button onClick={onReply} size="small" sx={styles.actionButton}>
              {`${replies} ${replies === 1 ? "Reply" : "replies"}`}
            </Button>
          </Box>
        </Box>
      </Box>
      {replies > 0 && (
        <Button
          sx={{
            backgroundColor: "var(--neutral-50)",
            border: "1px solid var(--neutral-200)",
            borderRadius: "8px",
            padding: "8px 16px",
            fontSize: "12px",
            lineHeight: "16px",
            fontWeight: "500",
            color: "var(--neutral-900)",
          }}
        >
          See Reply
        </Button>
      )}
    </Card>
  );
}
